import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import type {
  AIHealthMessage,
  AIHealthCompanionState,
  AIPipelineStatus,
  CompanionPanelMode,
  AIConversationMode,
  CompanionPageContext,
} from '../types/ai-health-companion-types';
import {
  streamHealthCompanionMessage,
  sendHealthCompanionMessage,
} from '../services/ai-health-companion-service';
import { assembleHealthContext } from '../services/health-context-service';
import type {
  DiagnosticPipelineStage,
} from '../types/diagnostic-types';
import { INITIAL_DIAGNOSTIC_STATE } from '../types/diagnostic-types';
import type { DiagnosticSSEEvent } from '../types/diagnostic-types';

// Diagnostic service imports with fallback handling
// These will be imported lazily when needed
let diagnosticServiceModule: any = null;

const getDiagnosticService = async () => {
  if (!diagnosticServiceModule) {
    try {
      diagnosticServiceModule = await import('../services/diagnostic-service');
    } catch (error) {
      console.warn('Diagnostic service not available:', error);
      // Return fallback implementations
      diagnosticServiceModule = {
        startDiagnosticSession: async () => {
          throw new Error('Diagnostic service is not yet implemented');
        },
        sendDiagnosticMessage: async () => {
          throw new Error('Diagnostic service is not yet implemented');
        },
        endDiagnosticSession: async () => {
          console.log('Diagnostic session ended (no service available)');
        },
      };
    }
  }
  return diagnosticServiceModule;
};

/**
 * Welcome message displayed when the AI Health Companion is first opened
 */
const WELCOME_MESSAGE: AIHealthMessage = {
  id: 'welcome',
  role: 'assistant',
  content:
    "Hi! I'm your KaiMed Health Companion. I can help you understand your health data, explain test results, check medication interactions, and provide personalized health insights. What would you like to know?",
  timestamp: new Date().toISOString(),
  suggestions: [
    'How are my vitals trending?',
    'Explain my latest test results',
    'Check my medication schedule',
    'What screenings am I due for?',
  ],
};

/**
 * Keywords that trigger diagnostic mode suggestion
 */
const SYMPTOM_KEYWORDS = [
  'pain', 'ache', 'hurt', 'sore', 'fever', 'cough', 'nausea',
  'dizzy', 'tired', 'fatigue', 'headache', 'vomit', 'bleeding',
  'rash', 'swelling', 'shortness of breath', 'chest pain',
  'symptom', 'sick', 'ill', 'unwell', 'feeling bad',
];

/**
 * Check if a message contains symptom keywords
 */
const containsSymptomKeywords = (message: string): boolean => {
  const lowerMessage = message.toLowerCase();
  return SYMPTOM_KEYWORDS.some((keyword) => lowerMessage.includes(keyword));
};

/**
 * Module-level reference to the abort function for streaming messages
 * Allows cancellation of in-progress streaming when a new message is sent
 */
let abortStreamingRef: (() => void) | null = null;

/**
 * AI Health Companion Store
 *
 * Manages the state and actions for the AI Health Companion feature, including:
 * - Message history with streaming support
 * - Pipeline status tracking for multi-stage AI processing
 * - Voice mode and conversation modes
 * - Health context integration
 * - Panel and full-page display modes
 * - Message bookmarking
 */
export const useAIHealthCompanionStore = create<AIHealthCompanionState>()(
  persist(
    (set, get) => ({
      // State
      messages: [WELCOME_MESSAGE],
      isLoading: false,
      isStreaming: false,
      pipelineStatus: null,
      error: null,
      isPanelOpen: false,
      panelMode: 'chat' as CompanionPanelMode,
      isFullPage: false,
      conversationMode: 'general' as AIConversationMode,
      isVoiceMode: false,
      isListening: false,
      isSpeaking: false,
      currentPage: '/',
      currentPageTitle: 'Home',
      pageContext: null,
      healthContext: null,
      healthContextLoading: false,
      bookmarkedMessageIds: [],

      // Diagnostic mode state
      isDiagnosticMode: false,
      diagnosticState: INITIAL_DIAGNOSTIC_STATE,
      showDiagnosticPrompt: false,

      // Actions

      /**
       * Send a message using non-streaming mode
       * Useful for simple requests or when streaming is not supported
       */
      sendMessage: async (content: string) => {
        const state = get();

        // Abort any in-progress streaming
        if (abortStreamingRef) {
          abortStreamingRef();
          abortStreamingRef = null;
        }

        // Create and add user message
        const userMessage: AIHealthMessage = {
          id: uuidv4(),
          role: 'user',
          content,
          timestamp: new Date().toISOString(),
        };

        set({
          messages: [...state.messages, userMessage],
          isLoading: true,
          error: null,
        });

        try {
          // Build conversation history (exclude system/welcome messages)
          const conversationHistory = state.messages
            .filter((msg) => msg.role !== 'system' && msg.id !== 'welcome')
            .map((msg) => ({
              role: msg.role as 'user' | 'assistant',
              content: msg.content,
            }));

          // Send message to API
          const response = await sendHealthCompanionMessage({
            message: content,
            conversation_history: conversationHistory,
            context: {
              current_page: state.pageContext?.path || state.currentPage,
              current_page_title:
                state.pageContext?.title || state.currentPageTitle,
            },
            health_context: state.healthContext || undefined,
            conversation_mode: state.conversationMode,
          });

          // Create assistant message from response
          const assistantMessage: AIHealthMessage = {
            id: uuidv4(),
            role: 'assistant',
            content: response.message,
            timestamp: new Date().toISOString(),
            sections: response.sections,
            citations: response.citations,
            actions: response.actions,
            suggestions: response.suggestions,
            emergencyDetected: response.emergency_detected,
            safetyDisclaimer: response.safety_disclaimer,
            confidenceScore: response.confidence_score,
            metadata: response.metadata,
          };

          set({
            messages: [...get().messages, assistantMessage],
            isLoading: false,
          });
        } catch (error) {
          console.error('Error sending message:', error);

          // Create error message
          const errorMessage: AIHealthMessage = {
            id: uuidv4(),
            role: 'assistant',
            content:
              'I apologize, but I encountered an error processing your request. Please try again.',
            timestamp: new Date().toISOString(),
            error:
              error instanceof Error
                ? error.message
                : 'An unexpected error occurred',
          };

          set({
            messages: [...get().messages, errorMessage],
            isLoading: false,
            error:
              error instanceof Error
                ? error.message
                : 'An unexpected error occurred',
          });
        }
      },

      /**
       * Send a message using streaming mode
       * Provides real-time updates as the AI generates the response
       */
      sendMessageStreaming: async (content: string) => {
        const state = get();

        // Abort any in-progress streaming
        if (abortStreamingRef) {
          abortStreamingRef();
          abortStreamingRef = null;
        }

        // Detect symptom keywords and show diagnostic prompt if appropriate
        if (
          !state.isDiagnosticMode &&
          !state.showDiagnosticPrompt &&
          containsSymptomKeywords(content)
        ) {
          set({ showDiagnosticPrompt: true });
        }

        // Create and add user message
        const userMessage: AIHealthMessage = {
          id: uuidv4(),
          role: 'user',
          content,
          timestamp: new Date().toISOString(),
        };

        // Create placeholder assistant message for streaming
        const assistantMessageId = uuidv4();
        const placeholderMessage: AIHealthMessage = {
          id: assistantMessageId,
          role: 'assistant',
          content: '',
          timestamp: new Date().toISOString(),
          isStreaming: true,
          streamedContent: '',
        };

        set({
          messages: [...state.messages, userMessage, placeholderMessage],
          isStreaming: true,
          error: null,
          pipelineStatus: null,
        });

        try {
          // Build conversation history
          const conversationHistory = state.messages
            .filter((msg) => msg.role !== 'system' && msg.id !== 'welcome')
            .map((msg) => ({
              role: msg.role as 'user' | 'assistant',
              content: msg.content,
            }));

          // Start streaming
          const abortFn = await streamHealthCompanionMessage(
            {
              message: content,
              conversation_history: conversationHistory,
              context: {
                current_page: state.pageContext?.path || state.currentPage,
                current_page_title:
                  state.pageContext?.title || state.currentPageTitle,
              },
              health_context: state.healthContext || undefined,
              conversation_mode: state.conversationMode,
            },
            {
              onChunk: (chunk) => {
                const currentMessages = get().messages;
                const lastMessageIndex = currentMessages.length - 1;
                const lastMessage = currentMessages[lastMessageIndex];

                if (lastMessage && lastMessage.id === assistantMessageId) {
                  const updatedMessage: AIHealthMessage = {
                    ...lastMessage,
                  };

                  // Update content
                  if (chunk.content) {
                    updatedMessage.streamedContent =
                      (updatedMessage.streamedContent || '') + chunk.content;
                  }

                  // Add sections
                  if (chunk.section) {
                    updatedMessage.sections = [
                      ...(updatedMessage.sections || []),
                      chunk.section,
                    ];
                  }

                  // Add citations
                  if (chunk.citation) {
                    updatedMessage.citations = [
                      ...(updatedMessage.citations || []),
                      chunk.citation,
                    ];
                  }

                  // Add actions
                  if (chunk.action) {
                    updatedMessage.actions = [
                      ...(updatedMessage.actions || []),
                      chunk.action,
                    ];
                  }

                  // Add suggestions
                  if (chunk.suggestions) {
                    updatedMessage.suggestions = [
                      ...(updatedMessage.suggestions || []),
                      ...chunk.suggestions,
                    ];
                  }

                  const updatedMessages = [...currentMessages];
                  updatedMessages[lastMessageIndex] = updatedMessage;

                  set({ messages: updatedMessages });
                }
              },

              onStageUpdate: (stage) => {
                const currentStatus = get().pipelineStatus;
                const updatedStatus: AIPipelineStatus = {
                  currentStage: stage,
                  stagesCompleted: currentStatus
                    ? [...currentStatus.stagesCompleted, stage]
                    : [stage],
                };
                set({ pipelineStatus: updatedStatus });
              },

              onComplete: (finalResponse) => {
                const currentMessages = get().messages;
                const lastMessageIndex = currentMessages.length - 1;
                const lastMessage = currentMessages[lastMessageIndex];

                if (lastMessage && lastMessage.id === assistantMessageId) {
                  const finalMessage: AIHealthMessage = {
                    ...lastMessage,
                    content: lastMessage.streamedContent || finalResponse.message,
                    isStreaming: false,
                    streamedContent: undefined,
                    sections: finalResponse.sections,
                    citations: finalResponse.citations,
                    actions: finalResponse.actions,
                    suggestions: finalResponse.suggestions,
                    emergencyDetected: finalResponse.emergency_detected,
                    safetyDisclaimer: finalResponse.safety_disclaimer,
                    confidenceScore: finalResponse.confidence_score,
                    metadata: finalResponse.metadata,
                  };

                  const updatedMessages = [...currentMessages];
                  updatedMessages[lastMessageIndex] = finalMessage;

                  set({
                    messages: updatedMessages,
                    isStreaming: false,
                    pipelineStatus: null,
                  });
                }

                abortStreamingRef = null;
              },

              onError: (error) => {
                console.error('Streaming error:', error);

                const currentMessages = get().messages;
                const lastMessageIndex = currentMessages.length - 1;
                const lastMessage = currentMessages[lastMessageIndex];

                if (lastMessage && lastMessage.id === assistantMessageId) {
                  // If streaming message has no content, remove it and add error message
                  if (!lastMessage.streamedContent) {
                    const errorMessage: AIHealthMessage = {
                      id: uuidv4(),
                      role: 'assistant',
                      content:
                        'I apologize, but I encountered an error processing your request. Please try again.',
                      timestamp: new Date().toISOString(),
                      error:
                        error instanceof Error
                          ? error.message
                          : 'An unexpected error occurred',
                    };

                    const updatedMessages = [...currentMessages];
                    updatedMessages[lastMessageIndex] = errorMessage;

                    set({
                      messages: updatedMessages,
                      isStreaming: false,
                      pipelineStatus: null,
                      error:
                        error instanceof Error
                          ? error.message
                          : 'An unexpected error occurred',
                    });
                  } else {
                    // Mark partial message as error
                    const errorMessage: AIHealthMessage = {
                      ...lastMessage,
                      content: lastMessage.streamedContent || '',
                      isStreaming: false,
                      streamedContent: undefined,
                      error:
                        error instanceof Error
                          ? error.message
                          : 'An unexpected error occurred',
                    };

                    const updatedMessages = [...currentMessages];
                    updatedMessages[lastMessageIndex] = errorMessage;

                    set({
                      messages: updatedMessages,
                      isStreaming: false,
                      pipelineStatus: null,
                      error:
                        error instanceof Error
                          ? error.message
                          : 'An unexpected error occurred',
                    });
                  }
                }

                abortStreamingRef = null;
              },
            }
          );

          // Store abort function
          abortStreamingRef = abortFn;
        } catch (error) {
          console.error('Error starting streaming:', error);

          // Remove placeholder message and add error message
          const currentMessages = get().messages;
          const errorMessage: AIHealthMessage = {
            id: uuidv4(),
            role: 'assistant',
            content:
              'I apologize, but I encountered an error processing your request. Please try again.',
            timestamp: new Date().toISOString(),
            error:
              error instanceof Error
                ? error.message
                : 'An unexpected error occurred',
          };

          set({
            messages: [
              ...currentMessages.slice(0, -1), // Remove placeholder
              errorMessage,
            ],
            isStreaming: false,
            pipelineStatus: null,
            error:
              error instanceof Error
                ? error.message
                : 'An unexpected error occurred',
          });

          abortStreamingRef = null;
        }
      },

      /**
       * Clear the conversation and reset to welcome message
       */
      clearConversation: () => {
        // Abort any in-progress streaming
        if (abortStreamingRef) {
          abortStreamingRef();
          abortStreamingRef = null;
        }

        set({
          messages: [WELCOME_MESSAGE],
          error: null,
          isLoading: false,
          isStreaming: false,
          pipelineStatus: null,
        });
      },

      /**
       * Toggle the companion panel open/closed
       */
      togglePanel: () => {
        set({ isPanelOpen: !get().isPanelOpen });
      },

      /**
       * Set the panel mode (chat, insights, history)
       */
      setPanelMode: (mode: CompanionPanelMode) => {
        set({ panelMode: mode });
      },

      /**
       * Toggle between panel and full-page mode
       */
      setFullPage: (isFullPage: boolean) => {
        set({ isFullPage });
      },

      /**
       * Set the current page context for contextual responses
       */
      setPageContext: (context: CompanionPageContext) => {
        set({
          pageContext: context,
          currentPage: context.path,
          currentPageTitle: context.title,
        });
      },

      /**
       * Set the conversation mode (general, medical, wellness, etc.)
       */
      setConversationMode: (mode: AIConversationMode) => {
        set({ conversationMode: mode });
      },

      /**
       * Load patient health context from the backend
       */
      loadHealthContext: async (patientId: string) => {
        set({ healthContextLoading: true });

        try {
          const context = await assembleHealthContext(patientId);
          set({
            healthContext: context,
            healthContextLoading: false,
          });
        } catch (error) {
          console.error('Error loading health context:', error);
          set({ healthContextLoading: false });
        }
      },

      /**
       * Retry the last user message
       * Useful when there's an error or the user wants a different response
       */
      retryLastMessage: async () => {
        const state = get();
        const messages = state.messages;

        // Find the last user message
        let lastUserMessageIndex = -1;
        for (let i = messages.length - 1; i >= 0; i--) {
          if (messages[i].role === 'user') {
            lastUserMessageIndex = i;
            break;
          }
        }

        if (lastUserMessageIndex === -1) {
          return; // No user message to retry
        }

        const lastUserMessage = messages[lastUserMessageIndex];
        const messagesBeforeRetry = messages.slice(0, lastUserMessageIndex);

        // Reset messages to before the last user message
        set({ messages: messagesBeforeRetry });

        // Resend the message using streaming
        await get().sendMessageStreaming(lastUserMessage.content);
      },

      /**
       * Start voice input (listening mode)
       */
      startVoiceInput: () => {
        set({ isListening: true, isVoiceMode: true });
      },

      /**
       * Stop voice input
       */
      stopVoiceInput: () => {
        set({ isListening: false });
      },

      /**
       * Toggle voice mode on/off
       */
      toggleVoiceMode: async () => {
        const currentMode = get().isVoiceMode;
        if (currentMode) {
          // Turning off voice mode - stop all voice activities
          set({
            isVoiceMode: false,
            isListening: false,
            isSpeaking: false,
          });
        } else {
          // Turning on voice mode
          set({ isVoiceMode: true });
        }
      },

      /**
       * Bookmark a message for later reference
       */
      bookmarkMessage: (messageId: string) => {
        const bookmarks = get().bookmarkedMessageIds;
        if (!bookmarks.includes(messageId)) {
          set({
            bookmarkedMessageIds: [...bookmarks, messageId],
          });
        }
      },

      /**
       * Remove a bookmark from a message
       */
      unbookmarkMessage: (messageId: string) => {
        const bookmarks = get().bookmarkedMessageIds;
        set({
          bookmarkedMessageIds: bookmarks.filter((id) => id !== messageId),
        });
      },

      /* ---------- Diagnostic Mode Actions ---------- */

      /**
       * Show the diagnostic mode consent prompt
       * Called when symptom keywords are detected
       */
      showDiagnosticModePrompt: () => {
        set({ showDiagnosticPrompt: true });
      },

      /**
       * Dismiss the diagnostic mode consent prompt
       */
      dismissDiagnosticPrompt: () => {
        set({ showDiagnosticPrompt: false });
      },

      /**
       * User accepts diagnostic mode consent
       */
      acceptDiagnosticConsent: () => {
        const state = get();
        set({
          diagnosticState: {
            ...state.diagnosticState,
            consentGiven: true,
          },
        });
      },

      /**
       * Enter diagnostic mode with initial complaint
       */
      enterDiagnosticMode: async (initialComplaint: string) => {
        const state = get();

        // Abort any in-progress streaming
        if (abortStreamingRef) {
          abortStreamingRef();
          abortStreamingRef = null;
        }

        try {
          // Set diagnostic mode active
          set({
            isDiagnosticMode: true,
            conversationMode: 'diagnostic',
            showDiagnosticPrompt: false,
            diagnosticState: {
              ...state.diagnosticState,
              status: 'starting',
            },
          });

          // Add system message explaining diagnostic mode
          const systemMessage: AIHealthMessage = {
            id: uuidv4(),
            role: 'system',
            content:
              'You have entered Diagnostic Mode. I will ask you targeted questions to understand your symptoms and provide medical guidance. Please remember: this is not a replacement for professional medical care. If you experience severe symptoms, call emergency services immediately.',
            timestamp: new Date().toISOString(),
          };

          // Add user's initial complaint
          const userMessage: AIHealthMessage = {
            id: uuidv4(),
            role: 'user',
            content: initialComplaint,
            timestamp: new Date().toISOString(),
          };

          // Create placeholder for assistant response
          const assistantMessageId = uuidv4();
          const placeholderMessage: AIHealthMessage = {
            id: assistantMessageId,
            role: 'assistant',
            content: '',
            timestamp: new Date().toISOString(),
            isStreaming: true,
            streamedContent: '',
          };

          set({
            messages: [
              ...get().messages,
              systemMessage,
              userMessage,
              placeholderMessage,
            ],
            isStreaming: true,
          });

          // Start diagnostic session
          const service = await getDiagnosticService();
          const response = await service.startDiagnosticSession({
            initial_complaint: initialComplaint,
            health_context: state.healthContext || undefined,
          });

          // Update diagnostic state with session info
          set({
            diagnosticState: {
              ...get().diagnosticState,
              sessionId: response.session_id,
              status: 'active',
              triageSeverity: response.triage_severity as any,
              exchangeCount: 1,
            },
          });

          // Update placeholder with response
          const currentMessages = get().messages;
          const lastMessageIndex = currentMessages.length - 1;
          const finalMessage: AIHealthMessage = {
            ...currentMessages[lastMessageIndex],
            content: response.message,
            isStreaming: false,
            streamedContent: undefined,
            suggestions: response.suggestions,
          };

          const updatedMessages = [...currentMessages];
          updatedMessages[lastMessageIndex] = finalMessage;

          set({
            messages: updatedMessages,
            isStreaming: false,
          });
        } catch (error) {
          console.error('Error entering diagnostic mode:', error);

          // Add error message
          const errorMessage: AIHealthMessage = {
            id: uuidv4(),
            role: 'assistant',
            content:
              'I apologize, but I encountered an error starting the diagnostic session. Please try again or contact your healthcare provider directly.',
            timestamp: new Date().toISOString(),
            error:
              error instanceof Error
                ? error.message
                : 'Failed to start diagnostic session',
          };

          set({
            messages: [...get().messages.slice(0, -1), errorMessage],
            isStreaming: false,
            isDiagnosticMode: false,
            conversationMode: 'general',
            diagnosticState: {
              ...INITIAL_DIAGNOSTIC_STATE,
              consentGiven: get().diagnosticState.consentGiven,
            },
            error:
              error instanceof Error
                ? error.message
                : 'Failed to start diagnostic session',
          });
        }
      },

      /**
       * Send a message in diagnostic mode
       */
      sendDiagnosticMessage: async (content: string) => {
        const state = get();

        // Check if session is active
        if (
          !state.isDiagnosticMode ||
          !state.diagnosticState.sessionId ||
          state.diagnosticState.status !== 'active'
        ) {
          console.error('No active diagnostic session');
          return;
        }

        // Abort any in-progress streaming
        if (abortStreamingRef) {
          abortStreamingRef();
          abortStreamingRef = null;
        }

        // Add user message
        const userMessage: AIHealthMessage = {
          id: uuidv4(),
          role: 'user',
          content,
          timestamp: new Date().toISOString(),
        };

        // Create placeholder for assistant response
        const assistantMessageId = uuidv4();
        const placeholderMessage: AIHealthMessage = {
          id: assistantMessageId,
          role: 'assistant',
          content: '',
          timestamp: new Date().toISOString(),
          isStreaming: true,
          streamedContent: '',
        };

        set({
          messages: [...state.messages, userMessage, placeholderMessage],
          isStreaming: true,
          error: null,
        });

        try {
          // Send diagnostic message with SSE
          const service = await getDiagnosticService();
          const abortFn = await service.sendDiagnosticMessage(
            {
              session_id: state.diagnosticState.sessionId,
              message: content,
            },
            {
              onEvent: (event: DiagnosticSSEEvent) => {
                const currentMessages = get().messages;
                const lastMessageIndex = currentMessages.length - 1;
                const lastMessage = currentMessages[lastMessageIndex];

                if (!lastMessage || lastMessage.id !== assistantMessageId) {
                  return;
                }

                const updatedMessage: AIHealthMessage = { ...lastMessage };
                const currentDiagnosticState = get().diagnosticState;

                switch (event.event_type) {
                  case 'stage':
                    // Update current pipeline stage
                    set({
                      diagnosticState: {
                        ...currentDiagnosticState,
                        currentPipelineStage: event.data
                          .stage as DiagnosticPipelineStage,
                      },
                    });
                    break;

                  case 'content':
                    // Append content
                    updatedMessage.streamedContent =
                      (updatedMessage.streamedContent || '') +
                      (event.data.content || '');
                    break;

                  case 'diagnosis_update':
                    // Update differential diagnosis
                    set({
                      diagnosticState: {
                        ...currentDiagnosticState,
                        differentialDiagnosis: event.data.differential || [],
                      },
                    });
                    break;

                  case 'triage_update':
                    // Update triage severity
                    set({
                      diagnosticState: {
                        ...currentDiagnosticState,
                        triageSeverity: event.data.severity,
                      },
                    });
                    break;

                  case 'symptom_update':
                    // Update extracted symptoms
                    set({
                      diagnosticState: {
                        ...currentDiagnosticState,
                        extractedSymptoms: event.data.symptoms || [],
                      },
                    });
                    break;

                  case 'red_flag':
                    // Add red flag
                    set({
                      diagnosticState: {
                        ...currentDiagnosticState,
                        redFlagsDetected: [
                          ...currentDiagnosticState.redFlagsDetected,
                          event.data.flag,
                        ],
                      },
                    });
                    break;

                  case 'emergency':
                    // Emergency detected
                    set({
                      diagnosticState: {
                        ...currentDiagnosticState,
                        status: 'emergency',
                      },
                    });
                    updatedMessage.emergencyDetected = true;
                    break;

                  case 'suggestion':
                    // Add suggestions
                    updatedMessage.suggestions = [
                      ...(updatedMessage.suggestions || []),
                      ...(event.data.suggestions || []),
                    ];
                    break;

                  case 'done':
                    // Finalize message
                    updatedMessage.content =
                      updatedMessage.streamedContent || '';
                    updatedMessage.isStreaming = false;
                    updatedMessage.streamedContent = undefined;

                    // Increment exchange count and clear pipeline stage
                    set({
                      diagnosticState: {
                        ...get().diagnosticState,
                        exchangeCount: get().diagnosticState.exchangeCount + 1,
                        currentPipelineStage: null,
                      },
                      isStreaming: false,
                    });
                    break;

                  case 'error':
                    // Handle error
                    updatedMessage.error = event.data.message || 'Unknown error';
                    updatedMessage.isStreaming = false;
                    set({
                      isStreaming: false,
                      error: event.data.message || 'Unknown error',
                      diagnosticState: {
                        ...get().diagnosticState,
                        currentPipelineStage: null,
                      },
                    });
                    break;
                }

                // Update message
                const updatedMessages = [...currentMessages];
                updatedMessages[lastMessageIndex] = updatedMessage;
                set({ messages: updatedMessages });
              },
            }
          );

          // Store abort function
          abortStreamingRef = abortFn;
        } catch (error) {
          console.error('Error sending diagnostic message:', error);

          // Remove placeholder and add error message
          const currentMessages = get().messages;
          const errorMessage: AIHealthMessage = {
            id: uuidv4(),
            role: 'assistant',
            content:
              'I apologize, but I encountered an error processing your message. Please try again.',
            timestamp: new Date().toISOString(),
            error:
              error instanceof Error
                ? error.message
                : 'An unexpected error occurred',
          };

          set({
            messages: [...currentMessages.slice(0, -1), errorMessage],
            isStreaming: false,
            error:
              error instanceof Error
                ? error.message
                : 'An unexpected error occurred',
          });

          abortStreamingRef = null;
        }
      },

      /**
       * Exit diagnostic mode
       */
      exitDiagnosticMode: async () => {
        const state = get();

        // Abort any in-progress streaming
        if (abortStreamingRef) {
          abortStreamingRef();
          abortStreamingRef = null;
        }

        // End session if active
        if (state.diagnosticState.sessionId) {
          try {
            const service = await getDiagnosticService();
            await service.endDiagnosticSession(state.diagnosticState.sessionId);
          } catch (error) {
            console.error('Error ending diagnostic session:', error);
          }
        }

        // Add system message
        const systemMessage: AIHealthMessage = {
          id: uuidv4(),
          role: 'system',
          content:
            'You have exited Diagnostic Mode. I can continue to assist you with general health questions.',
          timestamp: new Date().toISOString(),
        };

        set({
          messages: [...state.messages, systemMessage],
          isDiagnosticMode: false,
          conversationMode: 'general',
          diagnosticState: {
            ...INITIAL_DIAGNOSTIC_STATE,
            consentGiven: state.diagnosticState.consentGiven, // Preserve consent
          },
        });
      },
    }),
    {
      name: 'mediconnect-ai-companion',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        messages: state.messages,
        isPanelOpen: state.isPanelOpen,
        bookmarkedMessageIds: state.bookmarkedMessageIds,
        conversationMode: state.conversationMode,
        isDiagnosticMode: state.isDiagnosticMode,
        diagnosticState: state.diagnosticState,
      }),
    }
  )
);

/**
 * @deprecated Use useAIHealthCompanionStore instead
 * Provided for backward compatibility with existing code
 */
export const useAIAssistantStore = useAIHealthCompanionStore;
